import { Autocomplete, FormControl, Paper, Skeleton, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { listboxStyles, paperStyles } from '@/components/Selector/styles';
import { ShowAllToggleOption } from '@/components/ShowAllToggleOption';
import { UserOptionItem } from '@/components/UserOptionItem';
import { SHOW_ALL_TOGGLE_UID, TOGGLE_OPTION } from '@/configs/form';
import { useUserStore } from '@/configs/store';
import { useYaMetrika } from '@/entities/yaMetrika';
import { useGlobalUserFilterModel } from '@/features/globalUserFilter';
import { MuiColors } from '@/types/colors';
import { AnalyticsEvent } from '@/types/yametrika';
import { YandexUser } from '@/types/yandex';

import s from './SelectUser.module.scss';

type FormValues = {
  user: YandexUser | null;
};

/**
 * Селектор для выбора пользователя (глобальный фильтр).
 */
const SelectUser: React.FC = observer(() => {
  const { user, fullName } = useUserStore();
  const {
    loadingStage,
    setSelectedUser,
    selectedUser,
    options,
    setSuggestQuery,
    showAll,
    toggleShowAll,
    favoriteUsersModel,
    myUserId,
  } = useGlobalUserFilterModel();
  const { toggleFavoriteUser, isFavorite } = favoriteUsersModel;
  const { sendEvent } = useYaMetrika();

  const myself =
    user && myUserId
      ? {
          uid: myUserId,
          display: `${fullName} (я)`,
          email: user.email,
        }
      : null;

  const optionsWithToggle = useMemo(() => [TOGGLE_OPTION, ...options], [options]);

  const { control, reset } = useForm<FormValues>({
    defaultValues: {
      user: myself,
    },
  });

  useEffect(() => {
    if (selectedUser) {
      reset({ user: selectedUser });
    }
  }, [selectedUser, reset]);

  return (
    <FormControl size="small" sx={{ minWidth: 250 }}>
      <p className={s.secondary}>Пользователь</p>

      {loadingStage.isLoading ? (
        <Skeleton variant="rectangular" height={32} sx={{ borderRadius: '6px' }} />
      ) : (
        <Controller
          control={control}
          name="user"
          render={({ field }) => (
            <Autocomplete
              size="small"
              options={optionsWithToggle}
              filterOptions={(options) => options}
              getOptionLabel={(option) =>
                option.uid === SHOW_ALL_TOGGLE_UID ? '' : option.display
              }
              value={field.value}
              onOpen={() => {
                setSuggestQuery('');
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setSuggestQuery(newInputValue);
                }
              }}
              onChange={(_, newValue) => {
                if (newValue && newValue.uid === SHOW_ALL_TOGGLE_UID) {
                  return;
                }

                if (!newValue) {
                  field.onChange(myself);
                  setSelectedUser(myself);

                  return;
                }

                field.onChange(newValue);
                setSelectedUser(newValue);
                sendEvent(AnalyticsEvent.selectUser);
              }}
              disableClearable={field.value?.uid === myUserId}
              isOptionEqualToValue={(option, value) => option.uid === value?.uid}
              loading={loadingStage.isLoading}
              clearOnEscape
              blurOnSelect
              slots={{
                paper: Paper,
              }}
              slotProps={{
                paper: {
                  sx: paperStyles,
                },
                listbox: {
                  sx: {
                    ...listboxStyles,
                    paddingTop: '6px',
                    paddingBottom: '6px',
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Пользователь"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 32,
                      padding: 0,
                      '& fieldset': {
                        borderRadius: '6px',
                        borderColor: MuiColors.secondaryColor,
                      },
                      '& input': {
                        padding: '4px 8px',
                        height: 24,
                        boxSizing: 'border-box',
                        color: MuiColors.primaryColor,
                        fontFamily: 'Montserrat',
                      },
                    },
                  }}
                />
              )}
              loadingText="Загрузка пользователей..."
              noOptionsText="Пользователи не найдены"
              renderOption={(props, option) => {
                if (option.uid === SHOW_ALL_TOGGLE_UID) {
                  return (
                    <ShowAllToggleOption
                      key={option.uid}
                      checked={showAll}
                      onToggle={toggleShowAll}
                      props={props}
                    />
                  );
                }

                return (
                  <UserOptionItem
                    key={option.uid}
                    option={option}
                    currentUserId={myUserId ?? undefined}
                    isFavorite={isFavorite}
                    onFavoriteClick={toggleFavoriteUser}
                    props={props}
                  />
                );
              }}
            />
          )}
        />
      )}
    </FormControl>
  );
});

export default SelectUser;
